import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppLayout } from "./components/layout/AppLayout";
import "./styles/tally-dashboard.css";
import { LoginPage } from "./pages/auth/LoginPage";
import { SignupPage } from "./pages/auth/SignupPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { PlaidOAuthPage } from "./pages/PlaidOAuthPage";
import { DashboardPage } from "./pages/DashboardPage";
import { TransactionsPage } from "./pages/TransactionsPage";
import { BudgetPage } from "./pages/BudgetPage";
import { BillsPage } from "./pages/BillsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { CalendarSettingsPage } from "./pages/CalendarSettingsPage";
import { PlanPage } from "./pages/PlanPage";
import { InsightsPage } from "./pages/InsightsPage";
import { GoalsPage } from "./pages/GoalsPage";
import { NetWorthPage } from "./pages/NetWorthPage";
import { CoursesPage } from "./pages/CoursesPage";
import { CourseDetailPage } from "./pages/CourseDetailPage";
import { TransactionRulesPage } from "./pages/TransactionRulesPage";
import { SubscriptionsPage } from "./pages/SubscriptionsPage";
import { DebtPayoffPage } from "./pages/DebtPayoffPage";
import { CashFlowPage } from "./pages/CashFlowPage";
import { PricingPage } from "./pages/PricingPage";
import { LandingPage } from "./pages/LandingPage";
import { AdminPage } from "./pages/AdminPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { DocsPage } from "./pages/DocsPage";
import { DocsDetailPage } from "./pages/DocsDetailPage";
import { ArticlesPage } from "./pages/ArticlesPage";
import React from "react";
const ArticleDetailPage = React.lazy(() => import("./pages/ArticleDetail"));
const ArticlePreviewPage = React.lazy(() => import("./pages/ArticlePreview"));
const AffiliateLandingPage = React.lazy(() => import("./pages/AffiliateLandingPage"));
const AffiliateLoginPage = React.lazy(() => import("./pages/AffiliateLoginPage"));
const AffiliateDashboard = React.lazy(() => import("./pages/AffiliateDashboard"));
const RefRedirect = React.lazy(() => import("./pages/RefRedirect"));
const DemoPage = React.lazy(() => import("./pages/DemoPage"));
const AdvisorLoginPage = React.lazy(() => import("./pages/AdvisorLoginPage"));
const AdvisorPortal = React.lazy(() => import("./pages/AdvisorPortal"));
const AdvisorSettings = React.lazy(() => import("./pages/AdvisorSettings"));
const ForgotPasswordPage = React.lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/auth/ResetPasswordPage"));
import { usePageTracking } from "./utils/usePageTracking";
import "./App.css";

// Page tracking component - wraps routes to track page views
const PageTracker = ({ children }) => {
  usePageTracking();
  return children;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const RootGate = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return user ? <AppLayout /> : <LandingPage />;
};

const LandingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  // If logged in, go to dashboard
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing page canonically lives at "/"; /welcome redirects there */}
      <Route path="/welcome" element={<Navigate to="/" replace />} />
      
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/forgot" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><ForgotPasswordPage /></React.Suspense>} />
      <Route path="/reset" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><ResetPasswordPage /></React.Suspense>} />
      <Route path="/forgot-password" element={<Navigate to="/forgot" replace />} />
      <Route path="/reset-password" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><ResetPasswordPage /></React.Suspense>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/demo" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><DemoPage /></React.Suspense>} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/docs/:sectionId" element={<DocsDetailPage />} />
      <Route path="/articles" element={<ArticlesPage />} />
      <Route path="/articles/preview" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><ArticlePreviewPage /></React.Suspense>} />
      <Route path="/articles/:slug" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><ArticleDetailPage /></React.Suspense>} />
      
      {/* Affiliate routes */}
      <Route path="/affiliates" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><AffiliateLandingPage /></React.Suspense>} />
      <Route path="/affiliates/login" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><AffiliateLoginPage /></React.Suspense>} />
      <Route path="/affiliates/dashboard" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><AffiliateDashboard /></React.Suspense>} />
      <Route path="/affiliates/verify" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><AffiliateDashboard /></React.Suspense>} />
      <Route path="/ref/:code" element={<React.Suspense fallback={null}><RefRedirect /></React.Suspense>} />
      
      {/* Advisor Portal */}
      <Route path="/advisor/login" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><AdvisorLoginPage /></React.Suspense>} />
      <Route path="/advisor/dashboard" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><AdvisorPortal /></React.Suspense>} />
      <Route path="/advisor/verify" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><AdvisorPortal /></React.Suspense>} />
      <Route path="/advisor/settings" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}><AdvisorSettings /></React.Suspense>} />
      
      {/* Plaid OAuth redirect handler */}
      <Route path="/plaid-oauth" element={<ProtectedRoute><PlaidOAuthPage /></ProtectedRoute>} />
      
      {/* Admin route - separate from main app */}
      <Route path="/admin" element={<AdminPage />} />
      
      {/* Protected routes */}
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      
      <Route path="/" element={<RootGate />}>
        <Route index element={<DashboardPage />} />
        <Route path="plan" element={<PlanPage />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="budget" element={<BudgetPage />} />
        <Route path="bills" element={<BillsPage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="goals" element={<GoalsPage />} />
        <Route path="net-worth" element={<NetWorthPage />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="courses/:courseId" element={<CourseDetailPage />} />
        <Route path="transaction-rules" element={<TransactionRulesPage />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="debt-payoff" element={<DebtPayoffPage />} />
        <Route path="cash-flow" element={<CashFlowPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/calendar" element={<CalendarSettingsPage />} />
      </Route>
      
      {/* Catch all - root landing for non-auth, dashboard for auth */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PageTracker>
          <AppRoutes />
        </PageTracker>
        <Toaster 
          position="top-center" 
          richColors 
          closeButton
          toastOptions={{
            className: "font-sans",
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
